import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

function Timeline() {
  const steps = [
    {
      title: "Vault Deployment",
      desc: "QuantNexusCapital deploys a dedicated smart contract vault. Non-custodial by design, the vault is tokenized and ready for capital injection.",
      details: [
        "Vault architecture is deployed by QuantNexusCapital.",
        "Fee structure (management + performance above high watermark) is encoded directly into the contract.",
        "Vault tokens represent proportional ownership — secure, transparent, and flexible.",
      ],
    },
    {
      title: "Strategy Integration",
      desc: "The vault is now running on a dedicated strategy, with ownership represented by vault tokens.",
      details: [
        "Vault tokens track performance transparently and represent proportional ownership.",
        "Strategies operate fully on-chain, rules-based, with systematic SL/TP enforcement.",
        "Profits above the high watermark automatically trigger smart-contract fee distribution.",
      ],
    },
    {
      title: "Live Execution & Automation",
      desc: "Once capital is injected, the vault goes live — trades execute automatically, ownership remains tokenized, and fees distribute on-chain.",
      details: [
        "Allocators hold vault tokens that track equity continuously.",
        "Performance fees accrue only on profits above the high watermark, auto-distributed on-chain.",
        "Monthly reporting and equity tracking are verifiable directly on the blockchain.",
      ],
    },
  ];

  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="process"
      className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Section label */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-xs tracking-wider font-semibold uppercase text-cyan-700 bg-cyan-50 px-4 py-1 rounded-full">
            Process
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h2
          className="text-3xl md:text-5xl font-bold text-center mb-16 md:mb-20 text-gray-900"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          How It Works
        </motion.h2>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line only on desktop */}
          <div className="hidden md:block absolute left-1/2 top-0 -ml-0.5 w-1 h-full bg-gradient-to-b from-cyan-500 via-blue-500 to-cyan-600 rounded-full"></div>

          <div className="space-y-12 md:space-y-20">
            {steps.map((s, idx) => {
              const isOpen = openIndex === idx;

              return (
                <motion.div
                  key={idx}
                  className={`relative flex flex-col md:flex-row items-center ${
                    idx % 2 === 0 ? "md:flex-row-reverse" : ""
                  }`}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? 60 : -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.2 }}
                >
                  {/* Marker for desktop */}
                  <div className="hidden md:flex absolute left-1/2 -ml-6 items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold shadow-lg ring-4 ring-white z-10">
                    {idx + 1}
                  </div>

                  {/* Card */}
                  <div
                    className={`group relative bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-lg transition-all w-full md:w-5/12 p-6 md:p-8 overflow-hidden ${
                      idx % 2 === 0
                        ? "md:mr-auto md:pr-12"
                        : "md:ml-auto md:pl-12"
                    }`}
                  >
                    {/* Mobile marker (inside card) */}
                    <div className="flex md:hidden items-center mb-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold shadow-md mr-3">
                        {idx + 1}
                      </div>
                      <h3 className="text-lg font-semibold text-cyan-700">
                        {s.title}
                      </h3>
                    </div>

                    {/* Desktop title */}
                    <h3 className="hidden md:block text-xl font-semibold text-cyan-700 mb-3">
                      {s.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed">{s.desc}</p>

                    {/* Toggle */}
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : idx)}
                      className="mt-4 text-sm font-medium text-cyan-700 hover:text-cyan-900 transition-colors"
                    >
                      {isOpen ? "Hide details ▲" : "View details ▼"}
                    </button>

                    {/* Expandable details */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.ul
                          className="mt-4 pl-4 space-y-2 text-gray-600 text-sm list-disc"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4 }}
                        >
                          {s.details.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Footer note */}
        <motion.p
          className="mt-16 md:mt-20 text-center text-gray-500 max-w-2xl mx-auto text-sm md:text-base"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          Vaults are fully operational within 48 hours. Performance, reporting,
          and fee distribution remain fully on-chain, transparent, and
          automated.
        </motion.p>
      </div>
    </section>
  );
}

export default Timeline;
